'use client';

import React, { useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { GitCompare } from 'lucide-react';
import { useCompare } from '@/components/catalog/CompareContext';
import { usePriceRounding } from '@/hooks/usePriceRounding';
import { Star, Heart, ChevronRight, ShieldCheck, ShieldAlert, Anchor, User, Building2 } from 'lucide-react';
import BoatImagePlaceholder from './BoatImagePlaceholder';
import { Boat, SearchState } from '@/types';
import { t } from '@/locales';
import { useBoatTypes } from '@/hooks/useBoatTypes';
import { useDesignVariants } from '@/components/design/DesignVariantsProvider';

interface BoatCardProps {
  boat: Boat;
  searchFilters?: SearchState | null;
  isFavorite?: boolean;
  onToggleFavorite?: (boatId: string) => void;
  isUnavailable?: boolean;
  onClickUnavailable?: (boat: Boat) => void;
}

const BoatCard: React.FC<BoatCardProps> = ({ boat, searchFilters, isFavorite = false, onToggleFavorite, isUnavailable = false, onClickUnavailable }) => {
  const { getLabel } = useBoatTypes();
  const translateBoatType = (type: string) => getLabel(type) || type;
  const design = useDesignVariants();
  const cardStyle = design.boat_card_style;
  const isAirbnb = cardStyle === 'airbnb_square';
  const isMinimal = cardStyle === 'minimal';
  const compare = useCompare();
  const { formatPrice } = usePriceRounding();
  const inCompare = compare.isSelected(boat.id);
  const handleCompareToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    compare.toggle(boat);
  };

  const getDaysCount = () => {
    if (!searchFilters?.startDate || !searchFilters?.endDate) return 1;
    const start = new Date(searchFilters.startDate);
    const end = new Date(searchFilters.endDate);
    const diff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(1, diff + 1);
  };

  const days = getDaysCount();
  const enriched = boat as Boat & { priceForRangeTotal?: number; priceForRangeAvg?: number; priceForRangeDays?: number };
  const rangeTotal = enriched.priceForRangeTotal;
  const rangeAvg = enriched.priceForRangeAvg;
  const totalPrice = (rangeTotal != null && rangeTotal > 0) ? rangeTotal : boat.price * days;
  // Avg /giorno: priorità a server (priceForRangeAvg già calcolato sul range
  // backend), fallback a totalPrice/days, ultimo fallback boat.price.
  const avgDaily = (rangeAvg != null && rangeAvg > 0)
    ? rangeAvg
    : (days > 0 ? Math.round(totalPrice / days) : boat.price);

  // Prezzo visualizzato secondo rental_mode. price_per_day è SEMPRE il tariffario
  // giornaliero (anche per barche weekly/monthly: la settimana = ×7, il mese = ×30).
  // - daily:   prezzo/giorno (avgDaily col range, altrimenti boat.price)
  // - weekly:  tariffa settimanale = daily-rate ×7 → label "/settimana"
  // - monthly: tariffa mensile = daily-rate ×30 → label "/mese"
  // Col range usiamo avgDaily come base (riflette override/seasonal nella finestra)
  // ma esponiamo comunque la tariffa di periodo "a partire da", anche se il range
  // selezionato è più corto di una settimana/mese.
  // Solo stagionale: senza range usa il prezzo stagionale; assente (barca dark) → 0 = "su richiesta".
  const seasonFrom = (boat as { season_from_price?: number | null }).season_from_price;
  const dailyRate = (rangeTotal != null && rangeTotal > 0) ? avgDaily : (seasonFrom && seasonFrom > 0 ? seasonFrom : 0);
  const periodMultiplier = boat.rentalMode === 'weekly' ? 7 : boat.rentalMode === 'monthly' ? 30 : 1;
  const displayPrice = dailyRate * periodMultiplier;
  const periodLabel = boat.rentalMode === 'weekly'
    ? t('boatCardExt.perWeek')
    : boat.rentalMode === 'monthly'
      ? t('boatCardExt.perMonth')
      : t('boatCardExt.perDay');

  // Build URL with search params
  const boatUrl = useMemo(() => {
    const base = `/boat/${boat.slug ?? boat.id}`;
    if (!searchFilters) return base;

    const params = new URLSearchParams();
    if (searchFilters.startDate) params.set('startDate', searchFilters.startDate);
    if (searchFilters.endDate) params.set('endDate', searchFilters.endDate);
    if (searchFilters.passengers) params.set('guests', String(searchFilters.passengers));
    if (searchFilters.location) params.set('location', searchFilters.location);

    const queryString = params.toString();
    return queryString ? `${base}?${queryString}` : base;
  }, [boat.id, searchFilters]);

  const handleUnavailableClick = (e: React.MouseEvent) => {
    if (isUnavailable && onClickUnavailable) {
      e.preventDefault();
      onClickUnavailable(boat);
    }
  };

  const Wrapper = isUnavailable && onClickUnavailable ? 'div' : Link;
  const wrapperProps = isUnavailable && onClickUnavailable
    ? { onClick: handleUnavailableClick, role: 'button', tabIndex: 0 }
    : { href: boatUrl };

  return (
    <Wrapper
      {...(wrapperProps as any)}
      className={`group transition-all duration-500 flex flex-col h-full cursor-pointer ${
        isAirbnb
          ? 'bg-transparent rounded-2xl overflow-hidden hover:-translate-y-0.5'
          : isMinimal
            ? 'bg-transparent rounded-xl overflow-hidden hover:opacity-95'
            : `bg-white rounded-3xl overflow-hidden border hover:-translate-y-1.5 shadow-[0_20px_50px_-15px_rgba(15,23,42,0.18),0_8px_20px_-8px_rgba(15,23,42,0.1)] hover:shadow-[0_40px_80px_-20px_rgba(15,23,42,0.3),0_15px_30px_-10px_rgba(15,23,42,0.15)] ${
                isUnavailable ? 'border-amber-200 hover:border-amber-300' : 'border-slate-200 hover:border-slate-300'
              }`
      }`}
    >
      {/* Image Container */}
      <div className={`relative overflow-hidden ${isAirbnb ? 'aspect-square rounded-2xl' : isMinimal ? 'aspect-[4/3] rounded-xl' : 'aspect-video'}`}>
        {boat.image ? (
          <Image
            src={boat.image}
            alt={boat.name}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            className="object-cover transition-transform duration-1000 group-hover:scale-110"
          />
        ) : (
          <BoatImagePlaceholder className="w-full h-full" />
        )}
        {!isAirbnb && !isMinimal && (
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent opacity-60" />
        )}

        {/* Quality Badges */}
        <div className={`absolute top-5 left-5 right-5 flex justify-between items-start ${isMinimal ? 'hidden' : ''}`}>
          <div className="flex flex-col gap-2">
            {boat.lastMinuteEnabled && boat.lastMinuteDiscountPercent ? (
              <div data-cid="boat-card-last-minute" className="bg-amber-500 px-3 py-1.5 rounded-full shadow-lg">
                <span className="text-[9px] font-black uppercase tracking-widest text-white">Last Minute −{boat.lastMinuteDiscountPercent}%</span>
              </div>
            ) : null}
            {boat.featured && (
              <div className="bg-white/95 backdrop-blur-md px-3 py-1.5 rounded-full shadow-lg border border-white/20">
                <span className="text-[9px] font-black uppercase tracking-widest text-blue-600">{t('boatCardExt.premiere')}</span>
              </div>
            )}
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full shadow-sm backdrop-blur-md bg-white/90 border ${boat.licenseRequired ? 'text-amber-600 border-amber-100' : 'text-emerald-600 border-emerald-100'}`}>
              {boat.licenseRequired ? <ShieldAlert className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
              <span className="text-[9px] font-black uppercase tracking-widest">
                {boat.licenseRequired ? t('boatCardExt.licenseRequired') : t('boatCardExt.noLicense')}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCompareToggle}
              title={inCompare ? 'Rimuovi dal confronto' : 'Aggiungi al confronto'}
              className={`w-11 h-11 rounded-[1.2rem] backdrop-blur-md transition-all border flex items-center justify-center ${
                inCompare ? 'bg-blue-600 text-white border-blue-600' : 'bg-white/20 text-white border-white/20 hover:bg-white hover:text-blue-600'
              }`}
            >
              <GitCompare className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleFavorite?.(boat.id);
              }}
              className={`w-11 h-11 rounded-[1.2rem] backdrop-blur-md transition-all border border-white/20 flex items-center justify-center group/heart ${
                isFavorite ? 'bg-white text-red-500' : 'bg-white/20 text-white hover:bg-white hover:text-red-500'
              }`}
            >
              <Heart className={`w-5 h-5 transition-transform group-hover/heart:scale-110 ${isFavorite ? 'fill-red-500' : ''}`} />
            </button>
          </div>
        </div>

        {/* Unavailable Overlay */}
        {isUnavailable && (
          <div className="absolute bottom-6 right-6 bg-amber-500 text-white px-3 py-1.5 rounded-full shadow-lg z-10">
            <span className="text-[9px] font-black uppercase tracking-widest">{t('boatCardExt.unavailable')}</span>
          </div>
        )}

        {/* Pricing Float — solo classic */}
        {!isAirbnb && !isMinimal && (
        <div className="absolute bottom-6 left-6">
          <div className="flex flex-col gap-0.5">
            <span className="text-[9px] font-black text-white/70 uppercase tracking-[0.2em]">{t('boatCardExt.startingFrom')}</span>
            <div className="flex items-baseline gap-2">
              {displayPrice > 0 ? (
                <>
                  <span className="text-3xl font-bold text-white">
                    {formatPrice(displayPrice, { withSign: true })}
                  </span>
                  <span className="text-xs font-bold text-white/60 italic">
                    {periodLabel}
                  </span>
                </>
              ) : (
                <span className="text-lg font-bold text-white">—</span>
              )}
            </div>
            {!(rangeTotal != null && rangeTotal > 0) && (
              <span data-cid="boat-card-seasonal-hint" className="text-[8px] font-bold text-white/50 uppercase tracking-[0.15em]">
                {t('boatCardExt.seasonalRate')}
              </span>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Details Area */}
      <div className={`flex-grow flex flex-col ${isAirbnb ? 'pt-3 px-1' : isMinimal ? 'pt-2 px-1' : 'p-8'}`}>
        {!isAirbnb && !isMinimal && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-black text-blue-600 uppercase tracking-[0.3em]">{translateBoatType(boat.type)}</span>
            <span className="w-1.5 h-1.5 bg-slate-100 rounded-full" />
            <div className="flex items-center gap-1.5 bg-amber-50 px-2.5 py-1 rounded-lg">
              <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
              <span className="text-[10px] font-black text-amber-800">{boat.rating}</span>
            </div>
          </div>
        </div>
        )}

        {isAirbnb || isMinimal ? (
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className={`font-display font-semibold text-slate-900 leading-snug group-hover:text-slate-700 transition-colors ${isAirbnb ? 'text-base' : 'text-sm'}`}>{boat.name}</h3>
            {boat.rating > 0 && (
              <div className="flex items-center gap-1 shrink-0">
                <Star className="w-3.5 h-3.5 text-slate-900 fill-slate-900" />
                <span className="text-xs font-semibold text-slate-900">{boat.rating.toFixed(1)}</span>
              </div>
            )}
          </div>
        ) : (
          <h3 className="text-2xl font-display font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors leading-tight">{boat.name}</h3>
        )}

        {/* Owner-type badge: distingue COMPANY (Azienda) da VENDOR (Privato).
            Nessun nome reale esposto qui — solo categoria proprietario. */}
        {!isAirbnb && !isMinimal && boat.owner?.role === 'COMPANY' && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-[10px] font-black uppercase tracking-wider mb-2">
            <Building2 className="w-3 h-3" />
            Azienda
          </span>
        )}
        {!isAirbnb && !isMinimal && boat.owner?.role === 'VENDOR' && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-[10px] font-black uppercase tracking-wider mb-2">
            <User className="w-3 h-3" />
            Privato
          </span>
        )}

        {/* Charter fleet link for COMPANY owners */}
        {!isAirbnb && !isMinimal && boat.owner?.role === 'COMPANY' && boat.owner?.company_name && (
          <Link
            href={`/charter/${boat.owner.id}`}
            className="text-[10px] font-bold text-blue-500 hover:text-blue-700 transition-colors mb-2 block"
            onClick={e => e.stopPropagation()}
          >
            {boat.owner.company_name} — Vedi tutta la flotta &rarr;
          </Link>
        )}

        {/* Location */}
        <div className={`flex items-center gap-1.5 text-slate-500 ${isAirbnb || isMinimal ? 'text-xs mb-0.5' : 'text-sm mb-1'}`}>
          {!isAirbnb && !isMinimal && <Anchor className="w-4 h-4 text-blue-500" />}
          <span className={isAirbnb || isMinimal ? 'text-xs text-slate-500' : 'font-medium text-[12px]'}>{boat.port || boat.location}</span>
        </div>
        {!isAirbnb && !isMinimal && boat.latitude && boat.longitude ? (
          <p className="text-[10px] font-mono text-slate-400 mb-6 ml-6">
            {boat.latitude.toFixed(4)}°N, {boat.longitude.toFixed(4)}°E
          </p>
        ) : !isAirbnb && !isMinimal ? (
          <div className="mb-6" />
        ) : null}

        {/* Airbnb/Minimal footer pricing */}
        {(isAirbnb || isMinimal) && (
          <div className="mt-1 flex items-baseline gap-1.5">
            {displayPrice > 0 ? (
              <>
                <span className="text-sm font-semibold text-slate-900">
                  {formatPrice(displayPrice, { withSign: true })}
                </span>
                <span className="text-xs text-slate-500">
                  {`· ${periodLabel}`}
                </span>
              </>
            ) : (
              <span className="text-sm font-semibold text-slate-500">—</span>
            )}
            {!(rangeTotal != null && rangeTotal > 0) && (
              <span data-cid="boat-card-seasonal-hint" className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide">· {t('boatCardExt.seasonalRate')}</span>
            )}
          </div>
        )}

        {/* Dynamic Spec Grid */}
        {!isAirbnb && !isMinimal && (
        <div className={`grid gap-6 pt-6 border-t border-slate-50 mb-6 ${
          boat.cabins ? 'grid-cols-4' : 'grid-cols-3'
        }`}>
          <div className="space-y-1">
            <p className="text-base font-bold text-slate-900">{boat.length}m</p>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">{t('boatCardExt.length').toUpperCase()}</p>
          </div>
          <div className="space-y-1 text-center border-x border-slate-50 px-2">
            <p className="text-base font-bold text-slate-900">{boat.capacity}</p>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">{t('boatCardExt.passengers').toUpperCase()}</p>
          </div>
          {boat.cabins ? (
            <div className="space-y-1 text-center border-r border-slate-50 px-2">
              <p className="text-base font-bold text-slate-900">{boat.cabins}</p>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">{t('boatCardExt.cabins').toUpperCase()}</p>
            </div>
          ) : null}
          <div className="space-y-1 text-right">
            <p className="text-base font-bold text-slate-900">
              {boat.cabins && boat.bathrooms ? boat.bathrooms : boat.enginePower ? `${boat.enginePower} CV` : (boat.engine || '-')}
            </p>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">
              {boat.cabins && boat.bathrooms ? t('boatCardExt.bathrooms').toUpperCase() : t('boatCardExt.engine').toUpperCase()}
            </p>
          </div>
        </div>
        )}

        {/* Skipper Badge */}
        {!isAirbnb && !isMinimal && boat.skipperAvailable && (
          <div className="flex items-center gap-2 mb-6 px-3 py-2 bg-blue-50 rounded-xl border border-blue-100">
            <User className="w-4 h-4 text-blue-600" />
            <span className="text-[11px] font-bold text-blue-700 uppercase tracking-wider">
              {boat.skipperRequired ? t('boatCardExt.skipperIncluded') : t('boatCardExt.skipperAvailable')}
            </span>
          </div>
        )}

        {/* Action Call to Boat — solo classic */}
        {!isAirbnb && !isMinimal && (
        <span
          className={`w-full mt-auto flex items-center justify-center gap-3 py-4.5 rounded-2xl text-[11px] font-black uppercase tracking-[0.3em] transition-all shadow-xl active:scale-95 group/btn ${
            isUnavailable
              ? 'bg-amber-500 text-white shadow-amber-100 hover:bg-amber-600'
              : 'bg-slate-900 text-white shadow-slate-100 hover:bg-blue-600'
          }`}
        >
          {isUnavailable ? t('boatCardExt.viewAlternatives') : t('boatCardExt.exploreDetails')}
          <ChevronRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1.5" />
        </span>
        )}
      </div>
    </Wrapper>
  );
};

export default BoatCard;
